import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { ArrowLeft, Plus, Trash2, Edit2, Save, ListPlus, Loader2, ArrowUp, ArrowDown } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { FORMATIONS } from "../lib/classicFormations";

// Admin CRUD for the "Famous Lineups…" presets shown in the Classic
// Lineup Builder — same pattern as AdminClassicSquad.jsx. Each preset is
// a label + a formation shape (from FORMATIONS) + one player name per
// role slot; x/y/number/is_gk all come straight from the FORMATIONS
// template so the pitch layout always matches the builder itself.
export default function AdminClassicLineupPresets() {
  const [presets, setPresets] = useState([]);
  const [shirtDesigns, setShirtDesigns] = useState([]);
  const outfieldShirtDesigns = shirtDesigns.filter((d) => (d.role || "outfield") !== "goalkeeper");
  const gkShirtDesigns = shirtDesigns.filter((d) => d.role === "goalkeeper");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [label, setLabel] = useState("");
  const [formation, setFormation] = useState("4-4-2");
  const [playerNames, setPlayerNames] = useState({}); // { role: name }
  // Which shirt design (from Admin -> Classic Shirts) the builder's shirt
  // selector should auto-switch to when this preset is loaded — "" means
  // "no override, keep whatever's globally set as default".
  const [defaultShirtDesignId, setDefaultShirtDesignId] = useState("");
  // Same idea for the builder's separate GK shirt selector — a
  // `role: "goalkeeper"` design only.
  const [defaultGkShirtDesignId, setDefaultGkShirtDesignId] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [presetsRes, shirtsRes] = await Promise.all([
        api.get("/admin/classic-lineup-presets"),
        api.get("/admin/classic-shirt-designs"),
      ]);
      setPresets(presetsRes.data || []);
      setShirtDesigns(shirtsRes.data || []);
      setError("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setLabel("");
    setFormation("4-4-2");
    setPlayerNames({});
    setDefaultShirtDesignId("");
    setDefaultGkShirtDesignId("");
    setError("");
  };

  const startEdit = (preset) => {
    setEditingId(preset.id);
    setLabel(preset.label);
    setFormation(preset.formation);
    const names = {};
    (preset.players || []).forEach((p) => { names[p.role] = p.name || ""; });
    setPlayerNames(names);
    setDefaultShirtDesignId(preset.default_shirt_design_id || "");
    setDefaultGkShirtDesignId(preset.default_gk_shirt_design_id || "");
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleFormationChange = (f) => {
    setFormation(f);
    // Names typed against a role are kept if the new formation shares
    // that role; roles that don't exist in the new shape are dropped.
    setPlayerNames((prev) => {
      const roles = new Set(FORMATIONS[f].map((slot) => slot.role));
      const next = {};
      Object.keys(prev).forEach((role) => { if (roles.has(role)) next[role] = prev[role]; });
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!label.trim()) {
      setError("Please give this preset a name");
      return;
    }
    const template = FORMATIONS[formation];
    const players = template.map((slot) => ({
      ...slot,
      name: (playerNames[slot.role] || "").trim(),
    }));
    if (players.some((p) => !p.name)) {
      setError("Please fill in a name for every position");
      return;
    }
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const payload = {
        label: label.trim(),
        formation,
        players,
        default_shirt_design_id: defaultShirtDesignId || null,
        default_gk_shirt_design_id: defaultGkShirtDesignId || null,
      };
      if (editingId) {
        await api.put(`/admin/classic-lineup-presets/${editingId}`, payload);
        setSuccess("Preset updated successfully!");
      } else {
        await api.post("/admin/classic-lineup-presets", payload);
        setSuccess("Preset added successfully!");
      }
      resetForm();
      await load();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (presetId) => {
    if (!window.confirm("Delete this preset? It will disappear from the Classic Lineup Builder's Famous Lineups dropdown.")) return;
    setError("");
    setSuccess("");
    try {
      await api.delete(`/admin/classic-lineup-presets/${presetId}`);
      setSuccess("Preset removed successfully!");
      await load();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const move = async (presetId, direction) => {
    setError("");
    try {
      const r = await api.post(`/admin/classic-lineup-presets/${presetId}/move`, { direction });
      setPresets(r.data || []);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[var(--brand-ink)]">
      <Header />

      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <Link
              to="/admin"
              className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--brand-body)] uppercase tracking-wider hover:text-[var(--brand-ink)] mb-1"
            >
              <ArrowLeft size={12} /> Back to dashboard
            </Link>
            <h1 className="font-display text-2xl font-bold text-[var(--brand-ink)]" data-testid="admin-classic-lineup-presets-title">
              Classic Lineup Presets
            </h1>
            <p className="mt-1 text-sm text-[var(--brand-body)]">
              Manage the "Famous Lineups…" one-click presets shown in the Classic Lineup Builder.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="mb-8 rounded-md border border-black/10 bg-white p-5 shadow-sm" data-testid="admin-classic-lineup-preset-form">
          <h2 className="font-display text-lg font-bold mb-4 flex items-center gap-2">
            {editingId ? <Edit2 size={16} className="text-[#6CABDD]" /> : <ListPlus size={16} className="text-[#6CABDD]" />}
            {editingId ? "Edit Preset" : "Add New Preset"}
          </h2>

          {error ? (
            <p className="mb-4 rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm text-[var(--brand-ink)]" data-testid="admin-classic-lineup-preset-error">
              {error}
            </p>
          ) : null}
          {success ? (
            <p className="mb-4 rounded-sm border-l-2 border-emerald-500 bg-emerald-500/5 px-3 py-2 text-sm text-emerald-700 font-bold" data-testid="admin-classic-lineup-preset-success">
              {success}
            </p>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block label-kicker text-[var(--brand-body)]">Preset Name</label>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. 1987 FA Cup Final XI"
                className="mt-1 block w-full rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
                data-testid="admin-classic-lineup-preset-label-input"
              />
            </div>
            <div>
              <label className="block label-kicker text-[var(--brand-body)]">Formation</label>
              <select
                value={formation}
                onChange={(e) => handleFormationChange(e.target.value)}
                className="mt-1 block w-full rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
                data-testid="admin-classic-lineup-preset-formation-select"
              >
                {Object.keys(FORMATIONS).map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label className="block label-kicker text-[var(--brand-body)]">Default shirt for this lineup</label>
            <select
              value={defaultShirtDesignId}
              onChange={(e) => setDefaultShirtDesignId(e.target.value)}
              className="mt-1 block w-full max-w-sm rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17] sm:w-auto"
              data-testid="admin-classic-lineup-preset-shirt-select"
              title="When a fan loads this Famous Lineup, the builder's shirt selector auto-switches to this design"
            >
              <option value="">No override (use global default)</option>
              {outfieldShirtDesigns.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-[var(--brand-body)]">
              When a fan picks this preset from "Famous Lineups…", the shirt selector will automatically switch to this design.
            </p>
          </div>

          <div className="mt-4">
            <label className="block label-kicker text-[var(--brand-body)]">Default GK shirt for this lineup</label>
            <select
              value={defaultGkShirtDesignId}
              onChange={(e) => setDefaultGkShirtDesignId(e.target.value)}
              className="mt-1 block w-full max-w-sm rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17] sm:w-auto"
              data-testid="admin-classic-lineup-preset-gk-shirt-select"
              title="When a fan loads this Famous Lineup, the builder's GK shirt selector auto-switches to this design"
            >
              <option value="">No override (use global default)</option>
              {gkShirtDesigns.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-[var(--brand-body)]">
              Same idea, for the keeper's own selector — e.g. auto-switch to Ogrizovic's kit for "1987 FA Cup Final XI".
              {gkShirtDesigns.length === 0 ? " No goalkeeper shirts exist yet — add one in Admin -> Classic Shirts." : ""}
            </p>
          </div>

          <div className="mt-4">
            <label className="block label-kicker text-[var(--brand-body)] mb-2">
              Player for each position
            </label>
            <div className="grid gap-2 sm:grid-cols-2">
              {FORMATIONS[formation].map((slot) => (
                <div key={slot.role} className="flex items-center gap-2">
                  <span className="w-14 shrink-0 rounded-sm bg-[#002d75] px-2 py-1.5 text-center text-xs font-black text-white">
                    {slot.role}
                  </span>
                  <input
                    type="text"
                    maxLength={100}
                    value={playerNames[slot.role] || ""}
                    onChange={(e) => setPlayerNames((prev) => ({ ...prev, [slot.role]: e.target.value }))}
                    placeholder="Player name"
                    className="flex-1 min-w-0 rounded-sm border border-black/15 bg-white px-3 py-1.5 text-sm outline-none focus:border-[#0A0F17]"
                    data-testid={`admin-classic-lineup-preset-name-${slot.role}`}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="mt-5 flex items-center justify-between border-t border-black/5 pt-4">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-md bg-[#0A0F17] px-4 py-2 font-display text-sm font-bold uppercase tracking-wider text-white hover:bg-black disabled:opacity-40"
              data-testid="admin-classic-lineup-preset-submit"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {editingId ? "Save changes" : "Add Preset"}
            </button>
            {editingId ? (
              <button
                type="button"
                onClick={resetForm}
                className="label-kicker text-[var(--brand-body)] underline underline-offset-4 hover:text-[var(--brand-ink)]"
                data-testid="admin-classic-lineup-preset-cancel"
              >
                Cancel edit
              </button>
            ) : null}
          </div>
        </form>

        <div className="rounded-md border border-black/10 bg-white p-5 shadow-sm">
          <h2 className="font-display text-lg font-bold mb-4 flex items-center gap-2">
            <ListPlus size={16} className="text-[#6CABDD]" />
            Active Presets
          </h2>

          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((id) => (
                <div key={id} className="h-16 animate-pulse rounded-md border border-black/10 bg-[#F8FAFC]" />
              ))}
            </div>
          ) : presets.length === 0 ? (
            <p className="p-8 text-center text-sm text-[var(--brand-body)] italic">No presets yet — add your first Famous Lineup above.</p>
          ) : (
            <ul className="divide-y divide-black/5" data-testid="admin-classic-lineup-presets-list">
              {presets.map((preset, idx) => (
                <li key={preset.id} className="py-4 flex items-center justify-between gap-4 first:pt-0 last:pb-0">
                  <div>
                    <p className="font-display font-bold text-sm text-[var(--brand-ink)]">{preset.label}</p>
                    <p className="mt-0.5 text-xs text-[var(--brand-body)]">
                      {preset.formation}
                      {preset.default_shirt_design_id ? (
                        <span data-testid={`admin-classic-lineup-preset-shirt-badge-${preset.id}`}>
                          {" · "}Shirt: {shirtDesigns.find((d) => d.id === preset.default_shirt_design_id)?.name || "Unknown"}
                        </span>
                      ) : null}
                      {preset.default_gk_shirt_design_id ? (
                        <span data-testid={`admin-classic-lineup-preset-gk-shirt-badge-${preset.id}`}>
                          {" · "}GK: {shirtDesigns.find((d) => d.id === preset.default_gk_shirt_design_id)?.name || "Unknown"}
                        </span>
                      ) : null}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => move(preset.id, "up")}
                      disabled={idx === 0}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-black/10 text-[var(--brand-ink)] hover:border-[#0A0F17] hover:bg-black/5 disabled:opacity-30"
                      data-testid={`admin-classic-lineup-preset-move-up-${preset.id}`}
                    >
                      <ArrowUp size={12} />
                    </button>
                    <button
                      type="button"
                      onClick={() => move(preset.id, "down")}
                      disabled={idx === presets.length - 1}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-black/10 text-[var(--brand-ink)] hover:border-[#0A0F17] hover:bg-black/5 disabled:opacity-30"
                      data-testid={`admin-classic-lineup-preset-move-down-${preset.id}`}
                    >
                      <ArrowDown size={12} />
                    </button>
                    <button
                      type="button"
                      onClick={() => startEdit(preset)}
                      className="inline-flex h-8 items-center gap-1 rounded-md border border-black/10 px-3 text-xs font-semibold text-[var(--brand-ink)] hover:border-[#0A0F17] hover:bg-black/5"
                      data-testid={`admin-classic-lineup-preset-edit-${preset.id}`}
                    >
                      <Edit2 size={10} /> Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(preset.id)}
                      className="inline-flex h-8 items-center gap-1 rounded-md border border-border px-3 text-xs font-semibold text-[#E63946] hover:bg-[#E63946] hover:text-white"
                      data-testid={`admin-classic-lineup-preset-delete-${preset.id}`}
                    >
                      <Trash2 size={10} /> Remove
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
